import React, { useState, useEffect } from "react"
import { useParams, useHistory, useLocation } from "react-router-dom"
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  Badge,
} from "reactstrap"
import classnames from "classnames"

import Layout from "components/Layout"
import {
  getProductor,
  getDatosSisa,
  saveDatosSisa,
  consultarSisaAfip,
} from "api/agro"
import DatosGenerales from "./DatosGenerales"
import Establecimientos from "./Establecimientos"
import ActividadAgricola from "./ActividadAgricola"
import ActividadGanadera from "./ActividadGanadera"
// BDSJEA-167
import Tambo from "./Tambo"
import ScoringAgro from "./ScoringAgro"
import InformeAgropecuario from "./InformeAgropecuario"
import DatosSisa from "./DatosSisa"
import ProyeccionCrediticia from "./ProyeccionCrediticia"
// BDSJEA-466
import ReportePlanProductivo from "./ReportePlanProductivo"

import toastHandler from "toastHandler"
import useAuthorization from "hooks/useAuthorization"

// BDSJEA-466: botón «Reporte» + modal Plan Productivo. Tiene su propio estado (fuera de ProductorDetalle) para que abrir
// el modal no vuelva a montar las solapas de la ficha.
const BotonReporte = ({ idProductor }) => {
  const [abierto, setAbierto] = useState(false)
  return (
    <>
      <Button
        color="primary"
        outline
        className="me-2"
        onClick={() => setAbierto(true)}
      >
        <i className="mdi mdi-file-chart me-1" />
        Reporte
      </Button>
      <ReportePlanProductivo
        idProductor={idProductor}
        isOpen={abierto}
        toggle={() => setAbierto(false)}
      />
    </>
  )
}

const ProductorDetalle = () => {
  const { id } = useParams()
  const history = useHistory()
  const location = useLocation()
  const permissionBy = "agro.productor"
  const { puedeModificarObjeto } = useAuthorization()

  const queryParams = new URLSearchParams(location.search)
  const initialTab = queryParams.get("tab") || "datos"
  const initialEstablecimientoId = queryParams.get("establecimientoId")

  const [loading, setLoading] = useState(true)
  const [productor, setProductor] = useState(null)
  const [activeTab, setActiveTab] = useState(initialTab)
  const [error, setError] = useState(null)

  const [datosSisa, setDatosSisa] = useState(null)

  useEffect(() => {
    loadProductor()
    fetchDatosSisa()
  }, [id])

  const loadProductor = async () => {
    setLoading(true)
    try {
      const data = await getProductor(id)
      setProductor(data)
    } catch (error) {
      setError("Error al cargar productor: " + error.message)
      history.push("/agro/busqueda/productor")
    } finally {
      setLoading(false)
    }
  }

  // Cargar datos SISA existentes
  const fetchDatosSisa = async () => {
    try {
      const response = await getDatosSisa(id)
      setDatosSisa(response)
    } catch (error) {
      console.log("Sin datos SISA o error:", error)
    }
  }

  // Guardar datos SISA (manual)
  const handleSaveSisa = async (datos, archivo) => {
    try {
      const response = await saveDatosSisa(id, datos, archivo)
      setDatosSisa(response)
      return response
    } catch (error) {
      throw error
    }
  }

  // Consultar SISA en AFIP automáticamente
  const handleConsultarSisaAfip = async () => {
    try {
      const response = await consultarSisaAfip(id)
      if (response && response.estadoSisa) {
        setDatosSisa(response)
      }
      return response
    } catch (error) {
      throw error
    }
  }

  const tabs = [
    { id: "datos", label: "Datos Generales", icon: "mdi-account" },
    { id: "establecimientos", label: "Establecimientos", icon: "mdi-barn" },
    { id: "agricola", label: "Actividad Agrícola", icon: "mdi-sprout" },
    { id: "ganadera", label: "Actividad Ganadera", icon: "mdi-cow" },
    // BDSJEA-167: al mismo nivel que ganadería, no dentro. El tambo es una
    // actividad distinta con su propia economía, no una categoría de hacienda.
    { id: "tambo", label: "Tambo", icon: "mdi-water-outline" },
    { id: "scoring", label: "Scoring", icon: "mdi-chart-arc" },
    {
      id: "sisa",
      label: "SISA",
      icon: "mdi-clipboard-check-outline",
      badge: datosSisa?.estadoSisa,
    },
    {
      id: "proyeccion",
      label: "Proyección Crediticia",
      icon: "mdi-trending-up",
    },
    {
      id: "informeAgropecuario",
      label: "Informe Agropecuario",
      icon: "mdi-file-chart",
    },
  ]

  // Helper para color de badge SISA
  const getSisaBadgeColor = estado => {
    if (estado === 0) return "secondary"
    if (estado === 1) return "success"
    if (estado === 2) return "warning"
    if (estado === 3) return "danger"
    return null
  }

  const MainContent = () => {
    if (loading) {
      return (
        <div className="text-center py-5">
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          <p className="mt-3 text-muted">Cargando productor...</p>
        </div>
      )
    }

    if (!productor) {
      return (
        <Card>
          <CardBody className="text-center py-5">
            <i
              className="mdi mdi-alert text-warning"
              style={{ fontSize: 48 }}
            />
            <p className="mt-3">No se encontró el productor</p>
            <Button
              color="primary"
              onClick={() => history.push("/agro/busqueda/productor")}
            >
              Volver a Búsqueda
            </Button>
          </CardBody>
        </Card>
      )
    }

    return (
      <>
        {/* Header */}
        <Card className="mb-3">
          <CardBody>
            <Row className="align-items-center">
              <Col md={8}>
                <div className="d-flex align-items-center">
                  <div
                    className="avatar-lg me-3 rounded-circle d-flex align-items-center justify-content-center"
                    style={{ backgroundColor: "#e3f2fd" }}
                  >
                    <i
                      className="mdi mdi-account-tie text-primary"
                      style={{ fontSize: 32 }}
                    />
                  </div>
                  <div>
                    <h4 className="mb-1">{productor.razonSocial}</h4>
                    <p className="mb-0 text-muted">
                      CUIT: {productor.cuit}
                      <Badge
                        color={
                          productor.estadoCuit === "ACTIVO"
                            ? "success"
                            : "danger"
                        }
                        className="ms-2"
                      >
                        {productor.estadoCuit}
                      </Badge>
                    </p>
                  </div>
                </div>
              </Col>
              <Col md={4} className="text-end">
                <Button
                  color="secondary"
                  outline
                  className="me-2"
                  onClick={() => history.push("/agro/busqueda/productor")}
                >
                  <i className="mdi mdi-arrow-left me-1" />
                  Volver
                </Button>
                <BotonReporte idProductor={id} />
                <Button color="success" outline>
                  <i className="mdi mdi-file-pdf-box me-1" />
                  Exportar
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* Tabs de navegación */}
        <style>
          {`
            .nav-tabs-custom .nav-link.active {
              color: #495057 !important;
              background-color: #fff !important;
              font-weight: 600 !important;
              border-color: #dee2e6 #dee2e6 #fff !important;
            }
            .nav-tabs-custom .nav-link {
              color: #6c757d !important;
              cursor: pointer;
            }
            .nav-tabs-custom .nav-link:hover:not(.active) {
              color: #495057 !important;
              border-color: #e9ecef #e9ecef #dee2e6 !important;
            }
          `}
        </style>
        <Nav tabs className="nav-tabs-custom mb-3">
          {tabs.map(tab => (
            <NavItem key={tab.id}>
              <NavLink
                className={classnames({ active: activeTab === tab.id })}
                onClick={() => setActiveTab(tab.id)}
                style={{ cursor: "pointer" }}
              >
                <i className={`mdi ${tab.icon} me-1`} />
                {tab.label}
                {/* Badge para SISA */}
                {tab.badge !== null && tab.badge !== undefined && (
                  <Badge
                    color={getSisaBadgeColor(tab.badge)}
                    className="ms-1"
                    pill
                  >
                    {tab.badge}
                  </Badge>
                )}
              </NavLink>
            </NavItem>
          ))}
        </Nav>

        {/* Tab Content */}
        <TabContent activeTab={activeTab}>
          <TabPane tabId="datos">
            <DatosGenerales productor={productor} onUpdate={loadProductor} />
          </TabPane>
          <TabPane tabId="establecimientos">
            <Establecimientos
              idProductor={id}
              establecimientoId={initialEstablecimientoId}
            />
          </TabPane>
          <TabPane tabId="agricola">
            <ActividadAgricola idProductor={id} />
          </TabPane>
          <TabPane tabId="tambo">
            {activeTab === "tambo" && <Tambo idProductor={id} />}
          </TabPane>
          <TabPane tabId="ganadera">
            <ActividadGanadera idProductor={id} />
          </TabPane>
          <TabPane tabId="scoring">
            {activeTab === "scoring" && <ScoringAgro idProductor={id} />}
          </TabPane>
          <TabPane tabId="sisa">
            <DatosSisa
              idProductor={id}
              cuit={productor?.cuit}
              datosSisa={datosSisa}
              onSave={handleSaveSisa}
              onConsultarAfip={handleConsultarSisaAfip}
              readOnly={!puedeModificarObjeto("/agro/establecimientos")}
            />
          </TabPane>
          <TabPane tabId="proyeccion">
            <ProyeccionCrediticia idProductor={id} />
          </TabPane>
          <TabPane tabId="informeAgropecuario">
            <InformeAgropecuario idProductor={id} />
          </TabPane>
        </TabContent>
      </>
    )
  }

  return (
    <Layout
      components={[{ component: <MainContent /> }]}
      title={productor?.razonSocial || "Productor"}
      permissionBy={permissionBy}
    />
  )
}

export default ProductorDetalle
